/**
 * LocationFields - Location form fields
 *
 * Fields: home_location_id, current_location_id, current_area_id
 */

import { useMemo } from 'react'
import type { UseFormReturn } from 'react-hook-form'
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { useLocations } from '@/hooks/useLocations'
import { useHangars } from '@/hooks/useHangars'
import type { AircraftFormValues } from '../aircraft-form-schema'

interface LocationFieldsProps {
  form: UseFormReturn<AircraftFormValues>
  disabled?: boolean
}

export function LocationFields({ form, disabled = false }: LocationFieldsProps) {
  const { data: locations } = useLocations()
  const { data: hangars } = useHangars()

  const watchedCurrentLocation = form.watch('current_location_id')

  const locationOptions = useMemo(
    () =>
      (locations ?? []).map((location) => ({
        value: location.id,
        label: `${location.name} (${location.location_code})`,
      })),
    [locations]
  )

  const hangarOptions = useMemo(
    () =>
      (hangars ?? [])
        .filter((h) => h.location_id === watchedCurrentLocation)
        .map((hangar) => ({
          value: hangar.id,
          label: `${hangar.name} (${hangar.building_code})`,
        })),
    [hangars, watchedCurrentLocation]
  )

  return (
    <div className="space-y-4">
      <FormField
        control={form.control}
        name="home_location_id"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Home Location</FormLabel>
            <FormControl>
              <SearchableSelect
                options={locationOptions}
                value={field.value}
                onValueChange={field.onChange}
                placeholder="Select home location"
                searchPlaceholder="Search locations..."
                emptyMessage="No locations found."
                allowClear
                disabled={disabled}
              />
            </FormControl>
            <FormDescription>Where this aircraft is normally based</FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="current_location_id"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Current Location</FormLabel>
            <FormControl>
              <SearchableSelect
                options={locationOptions}
                value={field.value}
                onValueChange={(v) => {
                  field.onChange(v)
                  // Clear area when location changes
                  form.setValue('current_area_id', null)
                }}
                placeholder="Select current location"
                searchPlaceholder="Search locations..."
                emptyMessage="No locations found."
                allowClear
                disabled={disabled}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {watchedCurrentLocation && (
        <FormField
          control={form.control}
          name="current_area_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Current Building</FormLabel>
              <FormControl>
                <SearchableSelect
                  options={hangarOptions}
                  value={field.value}
                  onValueChange={field.onChange}
                  placeholder="Select building"
                  searchPlaceholder="Search buildings..."
                  emptyMessage="No buildings found."
                  allowClear
                  disabled={disabled}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </div>
  )
}
